import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import Navbar2 from '@/component/navbar2';
import BrandFooter from '@/components/BrandFooter';
import Stars from '@/components/Stars';
import TeamAvatar from '@/components/TeamAvatar';
import Dh from '@/components/Dh';
import { L7_MEDIA } from '@/data/landingMedia';
import {
  HTReveal, SHead, PhotoBand, AudienceGrid, StepList, AppFrame, DashGrid, DashCard, DashStart,
  DashTrack, DashRing, HumanBand, Stats, ClientStory,
  Compare, Faq, Ck, CtaBand, CORPORATE_AUDIENCE, type CmpRow,
} from '@/components/landing/HT';
import { AbConfirm, AbCredential, AbTempo, AbVerified } from '@/components/landing/AbstractIcons';

/* ============================================================================
   Conncct Corporate Services — connectcorporate.com/corporate

   Build of the corporate-services reference landing: the same "human touch"
   system as / and /accounting, in the orange variant (`.ht--orange`).

   The plans band stays live on `corporateServices/current.packages` — the doc
   /admin/corporate-pricing edits — so the admin's compliance packages keep
   surfacing here as well as on /corporate/pricing.
   ========================================================================== */

const PRICING = '/corporate/pricing';

const PILLS = [
  { label: 'Founders', dot: '#F5893F' },
  { label: 'Investors', dot: '#2E6BD6' },
  { label: 'SMEs', dot: '#15824E' },
  { label: 'Remote teams', dot: '#7A4FC0' },
  { label: 'Family offices', dot: '#E85C9A' },
  { label: 'Relocating founders', dot: '#0E9E8E' },
];

const STEPS = [
  { h: 'Choose your structure', p: 'On a free consultation we map mainland vs free zone, your activity, visa quota and budget, and price it instantly.' },
  { h: 'We form & file', p: 'Licence, establishment card, e-channel and visas, prepared and submitted to the right authorities, tracked end to end.' },
  { h: 'Your specialist keeps you compliant', p: 'Renewals, amendments, PRO and WPS payroll handled on a managed calendar by a real person, so nothing lapses.' },
];

const FACTS = [
  { b: '0%', h: 'Personal income tax', p: 'The UAE levies no tax on individual salaries or personal income.' },
  { b: '100%', h: 'Foreign ownership', p: 'On most mainland activities since 2021, no local partner required.' },
  { b: '40+', h: 'Free zones', p: 'Across the Emirates, matched to your activity and your budget.' },
  { b: '10-yr', h: 'Golden Visa', p: 'Long-term residence for investors and talent, which we help you qualify for.' },
];

const CMP: CmpRow[] = [
  ['Setup + visas + PRO in one place', 'yes', 'Sometimes', 'no'],
  ['Transparent, published pricing', 'yes', 'Quote only', 'yes'],
  ['Renewal & expiry tracking', 'yes', 'Manual', 'no'],
  ['Live progress dashboard', 'yes', 'no', 'no'],
  ['A named specialist on your file', 'yes', 'Varies', 'no'],
];

const FAQS = [
  { q: 'Mainland or free zone, which is right for me?', a: 'It depends on your activity, where your customers are, and how many visas you need. Mainland lets you trade directly across the UAE; free zones offer cost-effective packages and 100% ownership. On your free consultation we map both and recommend the best fit.' },
  { q: 'How long does company formation take?', a: 'Many free zone licences are issued within a few working days once documents are ready. Mainland and visa processing depend on the activity and approvals involved, we give you a realistic timeline upfront and track every step.' },
  { q: 'Can I own 100% of my company?', a: 'Yes, free zones have always allowed full foreign ownership, and since 2021 most mainland activities do too, with no local partner required.' },
  { q: 'Do you handle the residence visas as well?', a: 'Yes. We process residence, employment, family and dependent visas through e-channel (including entry permit, medical, Emirates ID and stamping) plus Golden and Green visas where you qualify.' },
  { q: 'What about renewals and ongoing PRO work?', a: 'We manage licence renewals, amendments, attestation, NOCs, penalty handling and WPS payroll on a tracked calendar, so nothing lapses after you launch.' },
];

/* Compliance packages — admin-editable via `corporateServices/current.packages`. */
type Pkg = {
  name: string; tag: string; desc: string; inherit: string; features: string[];
  priceYr: number; priceMo: number | null; discount: string | null; trial: string | null;
  govFees: boolean; popular: boolean;
};

const DFLT_PACKAGES: Pkg[] = [
  { name: 'Starter', tag: 'For new business setup', desc: 'For first-time founders who need the basics to launch with confidence.', inherit: 'Core features:', features: ['DED / free zone registration', 'Licence issuance', 'VAT registration'], priceYr: 1100, priceMo: null, discount: null, trial: '30-day free trial', govFees: true, popular: false },
  { name: 'Total Compliance', tag: '100% FTA-compliant', desc: 'For entrepreneurs who want to stay 100% compliant with UAE regulations.', inherit: 'Everything in Starter +', features: ['VAT registration + quarterly VAT filings', 'Accounting & bookkeeping dashboard', 'Corporate Tax registration & annual return (FTA)', 'Up to 100 bookkeeping transactions per month', 'Annual financial statements'], priceYr: 14399, priceMo: 1499, discount: '20% OFF', trial: null, govFees: true, popular: false },
  { name: 'Business-in-a-Box™', tag: 'Full compliance + dedicated accountant', desc: 'For entrepreneurs who want full legal, accounting and tax compliance, plus a dedicated accountant.', inherit: 'Everything in Total Compliance +', features: ['Up to 200 bookkeeping transactions per month', 'Quarterly compliance advisory session', 'Investor-ready reporting (deck financials, data room)', 'Priority support'], priceYr: 22999, priceMo: 2399, discount: '20% OFF', trial: null, govFees: true, popular: true },
];

export default function CorporateServices() {
  const [packages, setPackages] = useState<Pkg[]>(DFLT_PACKAGES);
  const [billing, setBilling] = useState<'mo' | 'yr'>('mo');

  useEffect(() => {
    const ref = doc(db, 'corporateServices', 'current');
    return onSnapshot(ref, snap => {
      const d = snap.data();
      if (d && Array.isArray(d.packages) && d.packages.length) setPackages(d.packages as Pkg[]);
    }, () => { /* keep defaults on error */ });
  }, []);

  return (
    <>
      <Navbar2 logoTo="/" signupTo="/signup" />

      <div className="ht ht--orange">
        <HTReveal />

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="hero">
          <div className="wrap hgrid">
            {/* `rise` lifts the copy in on load. The observer cannot: this is the
                fold, so HTReveal shows it at once. Same reason as the cards. */}
            <div className="rv rise">
              <span className="eyebrow">Company setup · Visas · PRO · UAE</span>
              <h1>Your UAE company, set up and <span className="g">running.</span></h1>
              <p className="sub">
                Mainland &amp; free zone licensing, residence visas, PRO and government paperwork, formed,
                filed and kept compliant by one team. <b>From trade licence to Golden Visa.</b>
              </p>
              <div className="pills">
                {PILLS.map(p => <span className="pill" key={p.label}><span className="d" style={{ background: p.dot }} />{p.label}</span>)}
              </div>
              <div className="hero-cta">
                <Link to={PRICING} className="btn btn-y">See your price <span className="arr">→</span></Link>
                <a href="#suite" className="btn btn-o">See what’s included</a>
              </div>
              <div className="trust">
                <span className="rate"><Stars /> <span><b style={{ color: 'var(--ink)' }}>Built by the team behind Wealthface</b> · 300+ businesses supported</span></span>
                <span className="badge"><AbVerified size={15} color="#15824E" /> One team · mainland &amp; free zone</span>
              </div>
            </div>

            <div className="visual rv d1">
              {/* The hero photo is the fold and the biggest thing on the page, so it is
                  fetched at once and at high priority. `loading="lazy"` here made it wait
                  for layout and then queue behind every other request on the page. */}
              <div className="photo">
                <img src={L7_MEDIA.corporateHero} alt="" loading="eager" fetchPriority="high" decoding="async" />
              </div>

              <div className="fcard c1">
                <div className="t"><AbConfirm size={14} color="#15824E" /> Trade licence issued</div>
                <div className="s">Dubai mainland</div>
                <span className="tagpill g">Done</span>
              </div>
              <div className="fcard c2">
                <div className="t"><AbCredential size={14} color="var(--amber)" /> Residence visa</div>
                <div className="s">Medical &amp; Emirates ID</div>
                <span className="tagpill a">In progress</span>
              </div>
              <div className="fcard c3">
                <div className="t">Your setup progress</div>
                <div className="mini">
                  <div><b style={{ color: '#D9662E' }}>80%</b><span>Launch-ready</span></div>
                  <div><b style={{ color: '#2E6BD6' }}>2</b><span>Visas</span></div>
                  <div><b style={{ color: '#15824E' }}>Live</b><span>Licence</span></div>
                </div>
              </div>
              <div className="fcard c4">
                <TeamAvatar name="Karim" initials="K" className="av" />
                <div><div className="t">Your PRO, Karim</div><span className="dotline">Online · here to help</span></div>
              </div>
              <div className="fcard c5">
                <span className="ic"><AbConfirm size={16} /></span>
                <div><div className="t">All under control</div><div className="s">Licence, visas &amp; renewals, handled.</div></div>
              </div>
            </div>
          </div>
        </section>

        {/* ── THE CORPORATE DESK ───────────────────────────────────────── */}
        <section className="sec alt" id="suite">
          <div className="wrap">
            <SHead
              k="The all-in-one corporate desk"
              h2="One team for everything it takes to operate in the UAE."
              lead="Stop chasing a formation agent, a typing centre and a PRO. Every part of setup and government paperwork, in one place."
            />
            <PhotoBand
              src={L7_MEDIA.teamOneTable}
              alt="Four colleagues talking something through around one table"
              title="One team around one table, not four suppliers around yours."
              lead="Formation, immigration, PRO and payroll sit in the same room, so nothing is handed off and lost between them."
            />
            <AudienceGrid items={CORPORATE_AUDIENCE} />
          </div>
        </section>

        {/* ── HOW WE WORK ──────────────────────────────────────────────── */}
        <section className="sec" id="how">
          <div className="wrap">
            <SHead
              k="How we work"
              h2="Three steps from idea to operating company."
              lead="Everything tracked on one platform: your licence, visas, PRO tasks and your dedicated specialist, in the same place."
            />
            <div className="howwrap">
              <AppFrame>
                <DashCard
                  k="Your order"
                  h="Mainland company · Dubai DED"
                  pill={<span className="tagpill a" style={{ margin: 0 }}>In progress</span>}
                >
                  <DashTrack steps={['Submitted', 'Documents', 'Licence', 'Visas']} at={3} />
                </DashCard>
                <DashGrid
                  main={
                    <DashCard k="Applications" h="What your desk is running">
                      <div className="drow">
                        <div className="l"><b>Trade licence</b><span>Issued: Dubai mainland</span></div>
                        <span className="tagpill g" style={{ margin: 0 }}>Done</span>
                      </div>
                      <div className="drow">
                        <div className="l"><b>Establishment card</b><span>Immigration registered</span></div>
                        <span className="tagpill g" style={{ margin: 0 }}>Done</span>
                      </div>
                      <div className="drow">
                        <div className="l"><b>Residence visa: founder</b><span>Medical &amp; Emirates ID stage</span></div>
                        <span className="tagpill a" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <AbTempo size={11} /> In progress
                        </span>
                      </div>
                    </DashCard>
                  }
                  side={
                    <DashCard
                      dark
                      k="Setup status"
                      pill={<span className="tagpill a" style={{ margin: 0 }}>In progress</span>}
                    >
                      <DashRing
                        pct={80}
                        label="Launch readiness"
                        checks={[
                          { label: 'Licence live', done: true },
                          { label: 'Immigration card', done: true },
                          { label: 'Visas stamped' },
                        ]}
                      />
                    </DashCard>
                  }
                />
              </AppFrame>
              <StepList steps={STEPS} />
            </div>
          </div>
        </section>

        {/* ── BUILT FOR THE UAE ────────────────────────────────────────── */}
        <section className="sec alt">
          <div className="wrap">
            <SHead
              k="Built for the UAE"
              h2="Local rules. Local desks. Done right."
              lead="Mainland, free zone, immigration, MOHRE. Every authority has its own process. We run them daily, so your formation, visas and renewals land first time, on time."
            />
            <PhotoBand
              src={L7_MEDIA.uaeDocuments}
              alt="Two people shaking hands over a completed licence file at a desk"
              title="The paperwork the counter actually accepts."
              lead="Activity codes, MOAs, NOCs and attestations, prepared the way each authority wants them, then walked through by someone who does it every week."
            />
            <div className="factsband">
              {FACTS.map((f, i) => (
                <div className="fb rv" style={{ transitionDelay: `${i * 60}ms` }} key={f.h}>
                  <b>{f.b}</b><h4>{f.h}</h4><p>{f.p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HUMAN TOUCH ──────────────────────────────────────────────── */}
        <section className="sec">
          <div className="wrap">
            <HumanBand
              title="A real PRO on your side, not a ticket number."
              lead="Every founder gets a named setup specialist and PRO who knows your file and answers in minutes. The platform tracks every step; a human runs it through the authorities."
              feats={['Dedicated setup specialist', 'Straight answers, not call-centre queues', 'Proactive renewal alerts', 'Government fees at cost, never marked up']}
              adviser="Karim"
              role="your PRO"
              avatar={<TeamAvatar name="Karim" initials="K" style={{ width: '100%', height: '100%' }} />}
              photo={{
                src: L7_MEDIA.proReview,
                alt: 'Two specialists reviewing a company file together',
                caption: 'Your file read by a person, before it reaches a counter.',
              }}
              chat={[
                { from: 'them', text: 'Good news, your trade licence is issued. I’ve started the establishment card and booked your medical for the founder visa.' },
                { from: 'you', text: 'Amazing, thank you' },
                { from: 'them', text: 'All tracked. Emirates ID next, then stamping, you’ll be fully set up in about 10 days. I’ll handle the renewals from here.' },
              ]}
            />
          </div>
        </section>

        {/* ── PROOF ────────────────────────────────────────────────────── */}
        <section className="sec alt">
          <div className="wrap">
            <SHead k="Real results" h2="Founders across the UAE set up and run with Conncct." />
            <Stats items={[
              { b: '300+', s: 'Businesses set up & serviced' },
              { b: '40+', s: 'Free zones covered' },
              { b: '4', s: 'Service pillars under one roof' },
              { b: '90 days', s: 'Renewal alerts before expiry' },
            ]} />
            <ClientStory
              quote="I set up my whole company from abroad without flying in once. Licence, visa, bank intro, Conncct handled all of it."
              name="Youssef A."
              note="SaaS founder · Dubai mainland"
              initial="Y"
              avatar={L7_MEDIA.youssefFace}
              photo={L7_MEDIA.youssefPortrait}
              caption="Youssef's licence, establishment card and visas, tracked end to end."
            />
          </div>
        </section>

        {/* ── WHY CONNCCT ──────────────────────────────────────────────── */}
        <section className="sec">
          <div className="wrap">
            <SHead k="Why Conncct" h2="One desk beats a pile of middlemen." />
            <Compare cols={['Conncct Corporate', 'Traditional agent', 'Doing it yourself']} rows={CMP} />
          </div>
        </section>

        {/* ── PLANS (live: corporateServices/current.packages) ─────────── */}
        <section className="sec alt" id="pricing">
          <div className="wrap">
            <SHead
              k="Transparent pricing"
              h2="Plans that keep you compliant and growing."
              lead="Setup, licence and visas are priced from your actual profile. These plans cover what comes after, staying compliant once you’re running."
            />
            <div style={{ textAlign: 'center', marginTop: -22, marginBottom: 30 }}>
              <div className="billtog">
                <button type="button" className={billing === 'mo' ? 'on' : ''} onClick={() => setBilling('mo')}>Monthly</button>
                <button type="button" className={billing === 'yr' ? 'on' : ''} onClick={() => setBilling('yr')}>
                  Annual <span className="sv">save 20%</span>
                </button>
              </div>
            </div>

            <div className="price3">
              {packages.map((p, i) => {
                const annualOnly = p.priceMo == null;
                const monthly = billing === 'mo' && !annualOnly;
                const amount = monthly ? p.priceMo! : p.priceYr;
                const unit = monthly ? '/ mo' : '/ yr';
                const showOff = !monthly && !annualOnly && p.discount;
                return (
                  <div className={`pcard rv${p.popular ? ' pop' : ''}`} style={{ transitionDelay: `${i * 70}ms` }} key={p.name + i}>
                    {p.popular && <span className="poptag">MOST POPULAR</span>}
                    <div className="pt">{p.name}</div>
                    <div className="pit">{p.tag || p.desc}</div>
                    <div className="pp">
                      <span><Dh />{Math.round(amount).toLocaleString('en-US')}</span>
                      <small>{unit}</small>
                      {showOff && <span className="off">{p.discount}</span>}
                    </div>
                    {p.trial && <div className="trial">{p.trial}</div>}
                    {p.govFees && <div className="gov">+ government fees, charged at cost</div>}
                    <div className="inh">{p.inherit}</div>
                    <ul>
                      {p.features.map(f => <li key={f}><span className="ck"><Ck size={12} /></span>{f}</li>)}
                    </ul>
                    <div className="pb">
                      <Link to={PRICING} className={`btn ${p.popular ? 'btn-y' : 'btn-o'}`}>
                        Choose {p.name.replace('™', '')} <span className="arr">→</span>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="alacarte rv">
              Setup priced separately: Dubai mainland from <b>AED 17,850</b> · residence visa <b>AED 6,090</b> ·
              PRO services from <b>AED 525</b>.
              <br /><Link to={PRICING}>Build your exact setup price →</Link>
            </p>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="sec" id="faq">
          <div className="wrap">
            <SHead k="Questions, answered" h2="What founders ask before they incorporate." />
            <Faq items={FAQS} />
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <CtaBand
          title="Set up the easy part. We’ll handle the paperwork."
          body="Book a free consultation and we’ll map exactly what your business needs to license, staff and stay compliant in the UAE, with a real specialist from day one."
          action={<Link to={PRICING} className="btn btn-y">See your price <span className="arr">→</span></Link>}
        />
      </div>

      <BrandFooter />
    </>
  );
}
